import * as React from "react";
import * as ReactQuery from "react-query";
import axios from "axios";

import {
  GraphqlWrappedData,
  InitialStateMinimumRequirements_GraphQl,
  useReactQueryClient,
} from "../../../Common";

import { DataSource_MyBag_QueryKey } from "../data-source--mybag-provider";

import { DataMutationMyBag_GiftcardRemove_QueryKey } from "./data-mutation--mybag-giftcard-remove-query-key-factory";

export interface DataMutationMyBagGiftcardRemoveConsumerInterface {
  data: DataMutationMyBagGiftcardRemove_Result["data"];
  request: DataMutationMyBagGiftcardRemove_Result["variables"];
  status: DataMutationMyBagGiftcardRemove_Result["status"];
}
const defaultValue =
  {} as unknown as DataMutationMyBagGiftcardRemoveConsumerInterface;
const DataMutationMyBagGiftcardRemoveContext =
  React.createContext(defaultValue);

export interface DataMutationMyBagGiftcardRemove_ControllerInterface {
  mutate: DataMutationMyBagGiftcardRemove_Result["mutate"];
  mutateAsync: DataMutationMyBagGiftcardRemove_Result["mutateAsync"];
}
const controllerDefaultValue =
  {} as unknown as DataMutationMyBagGiftcardRemove_ControllerInterface;
const DataMutationMyBagGiftcardRemove_ControllerContext = React.createContext(
  controllerDefaultValue
);

export interface DataMutationMyBagGiftcardRemove_Request {
  cart_id: string;
  gift_card_code: string;
}
export type DataMutationMyBagGiftcardRemove_Result =
  ReactQuery.UseMutationResult<
    DataMutationMyBagGiftcardRemoveResponse,
    unknown,
    DataMutationMyBagGiftcardRemove_Request,
    unknown
  >;

export interface DataMutationMyBagGiftcardRemoveResponse {
  cart?: {
    applied_gift_cards?: {
      code?: string;
    }[];
  };
}
export type GraphqlResponse_MyBagGiftcardRemove = GraphqlWrappedData<
  "removeGiftCardFromCart",
  DataMutationMyBagGiftcardRemoveResponse
>;

export interface DataMutationMyBagGiftcardRemoveProviderProps {
  useInitialState: () => {
    initialState: InitialStateMinimumRequirements_GraphQl;
  };
}
export const DataMutationMyBagGiftcardRemoveProvider: React.FunctionComponent<
  DataMutationMyBagGiftcardRemoveProviderProps
> = (props) => {
  const { initialState } = props.useInitialState();

  const queryClient = useReactQueryClient();

  const mutationKey: ReactQuery.MutationKey = React.useMemo(() => {
    return DataMutationMyBag_GiftcardRemove_QueryKey.mybagGiftcardRemove();
  }, []);

  const mutationFn: ReactQuery.MutationFunction<
    DataMutationMyBagGiftcardRemoveResponse,
    DataMutationMyBagGiftcardRemove_Request
  > = React.useCallback(
    async (request) => {
      const { cart_id, gift_card_code } = request;

      const gqlQuery = `
        mutation {
          removeGiftCardFromCart(
            input: {
              cart_id: "${cart_id}"
              gift_card_code: "${gift_card_code}"
            }
          ) {
            cart {
              applied_gift_cards {
                code
              }
            }
          }
        }
      `;

      const headers = {
        "Content-Type": "application/json",
        Store: initialState.store_code,
      };

      const { data } = await axios.post<GraphqlResponse_MyBagGiftcardRemove>(
        "/graphql",
        { query: gqlQuery },
        { headers }
      );

      let errorMessage: string | undefined;
      if (data) {
        if (data.errors) {
          if (Array.isArray(data.errors) && data.errors.length > 0) {
            const firstError = data.errors[0];
            if (firstError) {
              errorMessage = firstError.message;
            }
          }
        }
      }
      if (errorMessage) {
        throw new Error(errorMessage);
      }

      const { data: gqlResponseData } = data || {};
      const { removeGiftCardFromCart } = gqlResponseData || {};

      (window as any).require(
        ["Magento_Checkout/js/model/step-navigator"],
        (stepNavigator: any) => {
          const currentStepIsStale = true;
          stepNavigator.handleHash(currentStepIsStale);
        }
      );

      return removeGiftCardFromCart;
    },
    [initialState]
  );

  const onSuccess = React.useCallback(() => {
    queryClient.invalidateQueries(DataSource_MyBag_QueryKey.mybagBaseQuery);
  }, []);

  const mutation = ReactQuery.useMutation(mutationKey, mutationFn, {
    onSuccess,
  });

  const value: DataMutationMyBagGiftcardRemoveConsumerInterface = React.useMemo(
    () => ({
      data: mutation.data,
      request: mutation.variables,
      status: mutation.status,
      error: mutation.error,
    }),
    [mutation.data, mutation.variables, mutation.status, mutation.error]
  );
  const controllerValue: DataMutationMyBagGiftcardRemove_ControllerInterface =
    React.useMemo(
      () => ({
        mutate: mutation.mutate,
        mutateAsync: mutation.mutateAsync,
      }),
      [mutation, mutation.mutateAsync]
    );
  return (
    <DataMutationMyBagGiftcardRemoveContext.Provider value={value}>
      <DataMutationMyBagGiftcardRemove_ControllerContext.Provider
        value={controllerValue}
      >
        {props.children || []}
      </DataMutationMyBagGiftcardRemove_ControllerContext.Provider>
    </DataMutationMyBagGiftcardRemoveContext.Provider>
  );
};

export const useDataMutationMyBagGiftcardRemove = () => {
  const context = React.useContext(DataMutationMyBagGiftcardRemoveContext);
  if (context === defaultValue) {
    throw new Error(
      "useDataMutationMyBagGiftcardRemove must be used within a DataMutationMyBagGiftcardRemoveProvider"
    );
  }
  return context;
};

export const useDataMutationMyBagGiftcardRemove_asController = () => {
  const context = React.useContext(
    DataMutationMyBagGiftcardRemove_ControllerContext
  );
  if (context === controllerDefaultValue) {
    throw new Error(
      "useDataMutationMyBagGiftcardRemove_asController must be used within a DataMutationMyBagGiftcardRemoveProvider"
    );
  }
  return context;
};
